'use client'

import { FC } from 'react'
import { PriceCardList, useSelectTickets } from '@/root/modules/shared/SelectTicketPage'
import { BottomNavButtons } from '@/root/components/tuuleliinid'
import { CartCard } from '@/root/modules/tuuleliinid/SelectTicketPage/components/CartCard/CartCard'
import { TripInfoSummary } from '@/root/modules/tuuleliinid/SelectTicketPage/components/TripInfoSummary/TripInfoSummary'
import { ReservationTimer } from '@/root/modules/tuuleliinid/SelectTicketPage/components/ReservationTimer/ReservationTimer'
import { Loader } from '@/root/ui/tuuleliinid'
import { Banner } from '@/root/ui/tuuleliinid/Banner/Banner'
import { useGetTranslations } from '@/root/shared-hooks'
import { CountryItem, Reservation, SailPackage } from '@/root/shared-types'
import type { SailRefParam } from '@/root/shared-types/selectTicketPage/selectTicket'

interface PricesProps {
  countries: CountryItem[]
  routeBaseInfo: SailPackage
  sailParams: Array<SailRefParam>
  reservation?: Reservation
}

export const Prices: FC<PricesProps> = ({
  countries,
  sailParams = [],
  routeBaseInfo,
  reservation: initialReservation,
}) => {
  const { getTranslation } = useGetTranslations()
  const {
    prices,
    pricesBack,
    isLoading,
    errorMessage,
    clearError,
    blocked,
    expireAt,
    reservation,
    ports,
    backHref,
    nextDisabled,
    hidePassengerDataForm,
    initialQuantityByCode,
    initialPassengersByCode,
    seqNsByCode,
    residentMaxQty,
    passengerMaxQty,
    nonResidentMaxByCode,
    bicycleQty,
    handleInc,
    handleDec,
    handleSetPassengerQuantity,
    handleVehicleInc,
    handleVehicleDec,
    handleSetVehicleQuantity,
    handleResidentInc,
    handleResidentDec,
    handleResidentIdVerified,
    handlePassengerSaved,
    handlePassengerDeleted,
    handleFormStateChange,
    handleCheckout,
  } = useSelectTickets({ sailParams, routeBaseInfo, reservation: initialReservation })

  if (isLoading && !prices) {
    return (
      <div>
        <div className="max-w-content mx-auto flex w-full flex-col items-start gap-8">
          <h1 className="text-primary-base w-full pt-10 text-center text-2xl font-bold">
            {getTranslation('selectTicketStep')}
          </h1>
          <div className="flex w-full justify-center py-4">
            <Loader />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="max-w-content mx-auto flex w-full flex-col items-start gap-8">
        <h1 className="text-primary-base w-full pt-10 text-center text-2xl font-bold">
          {getTranslation('selectTicketStep')}
        </h1>

        {errorMessage && (
          <Banner
            variant="error"
            className="w-full"
            onClose={clearError}
            closeLabel={getTranslation('bannerClose')}
            body={errorMessage}
          />
        )}

        <div className="flex w-full flex-col items-start gap-5 lg:flex-row">
          {/* Left column: price cards */}
          <div className="flex w-full flex-col gap-5 lg:flex-1">
            {prices?.prices && (
              <>
                <PriceCardList
                  prices={prices.prices}
                  priceFilter={(price) => price.priceCategory.isResident}
                  maxTicketQty={residentMaxQty}
                  countries={countries}
                  departureDate={prices.sailRef.departureDate}
                  route={prices.sailRef.route}
                  onResidentInc={handleResidentInc}
                  onResidentDec={handleResidentDec}
                  onResidentIdVerified={handleResidentIdVerified}
                  onPassengerSaved={handlePassengerSaved}
                  onFormStateChange={handleFormStateChange}
                  onPassengerDeleted={handlePassengerDeleted}
                  disabled={blocked}
                  initialQuantityByCode={initialQuantityByCode}
                  initialPassengersByCode={initialPassengersByCode}
                  seqNsByCode={seqNsByCode}
                  hidePassengerDataForm={hidePassengerDataForm}
                />
                <PriceCardList
                  prices={prices.prices}
                  priceFilter={(price) => !price.priceCategory.isResident}
                  maxTicketQty={passengerMaxQty}
                  maxTicketQtyByCode={nonResidentMaxByCode}
                  countries={countries}
                  onInc={handleInc}
                  onDec={handleDec}
                  onPassengerSaved={handlePassengerSaved}
                  onFormStateChange={handleFormStateChange}
                  onPassengerDeleted={handlePassengerDeleted}
                  onSetQuantity={handleSetPassengerQuantity}
                  disabled={blocked}
                  initialQuantityByCode={initialQuantityByCode}
                  initialPassengersByCode={initialPassengersByCode}
                  seqNsByCode={seqNsByCode}
                  hidePassengerDataForm={hidePassengerDataForm}
                />
              </>
            )}
            {prices?.carDeckPrices && (
              <PriceCardList
                prices={prices.carDeckPrices}
                maxTicketQty={bicycleQty}
                onInc={handleVehicleInc}
                onDec={handleVehicleDec}
                onSetQuantity={handleSetVehicleQuantity}
                disabled={blocked}
                initialQuantityByCode={initialQuantityByCode}
                hidePassengerDataForm={hidePassengerDataForm}
              />
            )}
          </div>

          {/* Right column: trip info, cart summary */}
          <hr className="border-primary-base hidden w-full md:block lg:hidden" />
          <div className="relative flex w-full flex-col gap-5 md:flex-row lg:sticky lg:top-8 lg:w-96 lg:flex-col lg:self-start">
            {blocked && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/20">
                <Loader />
              </div>
            )}
            {prices && (
              <>
                <div className="md:w-1/2 lg:w-full">
                  {expireAt && (
                    <div className="mb-4">
                      <ReservationTimer expireAt={expireAt} />
                    </div>
                  )}
                  <TripInfoSummary
                    routeFrom={ports.from}
                    routeTo={ports.to}
                    timestamp={prices.departureAt}
                    timestampBack={pricesBack?.departureAt}
                  />
                </div>
                <hr className="border-primary-base w-full md:hidden lg:block" />
              </>
            )}
            <div className="md:w-1/2 lg:w-full">
              <CartCard reservation={reservation} />
            </div>
          </div>
          <hr className="border-primary-base hidden w-full md:block lg:hidden" />
        </div>

        <BottomNavButtons
          className="mt-12 mb-8 p-0"
          backHref={backHref}
          backTitle={getTranslation('backButton')}
          backSubtitle={getTranslation('goBackChangeDateTimeSubtitle')}
          nextTitle={getTranslation('continueLabel')}
          nextSubtitle={getTranslation('ticketsNextSubtitle')}
          nextDisabled={nextDisabled}
          onNext={handleCheckout}
        />
      </div>
    </div>
  )
}
